import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { DailyPuzzleOut, DailyResultIn, DailyResultOut } from '../api/types';
import { boardFromString, type Difficulty } from '../sudoku';
import { PlayScreen } from '../components/PlayScreen';
import { deserialize, initGame } from '../game/state';
import { loadLocal } from '../game/storage';
import { enqueueResult, flushResults, hasPending } from '../game/resultQueue';
import type { GameState } from '../game/types';

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard', 'expert'];

function buildInitial(puzzle: DailyPuzzleOut): GameState {
  const key = `daily:${puzzle.date}:${puzzle.difficulty}`;
  const stored = loadLocal(key);
  if (stored && stored.givens === puzzle.givens) return deserialize(stored);
  return initGame({
    key,
    kind: 'daily',
    difficulty: puzzle.difficulty,
    givens: boardFromString(puzzle.givens),
  });
}

export function Daily() {
  const [difficulty, setDifficulty] = useState<Difficulty>('easy');
  const [initial, setInitial] = useState<GameState | null>(null);
  const [puzzle, setPuzzle] = useState<DailyPuzzleOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DailyResultOut | null>(null);
  // A completion is held locally because it couldn't be submitted yet (offline).
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setInitial(null);
    setPuzzle(null);
    setError(null);
    setResult(null);
    setPending(false);
    // The service worker serves the daily payload from cache when offline, so a
    // puzzle loaded earlier today is still playable. Only a never-fetched puzzle
    // fails here.
    api<DailyPuzzleOut>(`/daily/today?difficulty=${difficulty}`)
      .then((p) => {
        if (cancelled) return;
        setPuzzle(p);
        setInitial(buildInitial(p));
        setPending(hasPending(p.date, p.difficulty));
      })
      .catch(() => {
        if (cancelled) return;
        setError(
          navigator.onLine
            ? 'The daily puzzle needs a connection. Try again online.'
            : "You’re offline and today’s puzzle isn’t downloaded yet. Open it once online, then it’s playable offline.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [difficulty]);

  // Flush any held results on mount and when the connection returns. The server
  // computes the score, so a reflected result here is the authoritative one.
  useEffect(() => {
    if (!puzzle) return;
    let cancelled = false;
    const flush = () => {
      void flushResults().then((done) => {
        if (cancelled) return;
        const out = done.get(`${puzzle.date}:${puzzle.difficulty}`);
        if (out) {
          setResult(out);
          setPending(false);
        }
      });
    };
    window.addEventListener('online', flush);
    flush();
    return () => {
      cancelled = true;
      window.removeEventListener('online', flush);
    };
  }, [puzzle]);

  const submit = (state: GameState) => {
    if (!puzzle) return;
    const payload: DailyResultIn = {
      difficulty: puzzle.difficulty,
      time_ms: state.elapsedMs,
      hints_used: state.hintsUsed,
      mistakes: state.mistakes,
    };
    void api<DailyResultOut>(`/daily/${puzzle.date}/result`, { method: 'POST', body: payload })
      .then((r) => {
        setResult(r);
        setPending(false);
      })
      .catch(() => {
        // Offline (or server unreachable): hold it and submit on reconnect.
        enqueueResult({ date: puzzle.date, payload });
        setPending(true);
      });
  };

  return (
    <div className="daily">
      <nav className="diff-tabs" aria-label="Daily difficulty">
        {DIFFICULTIES.map((d) => (
          <button
            key={d}
            type="button"
            className={`diff-tab ${d} ${d === difficulty ? 'active' : ''}`}
            aria-pressed={d === difficulty}
            onClick={() => setDifficulty(d)}
          >
            {d}
          </button>
        ))}
      </nav>

      {error ? (
        <p className="notice">{error}</p>
      ) : !initial || !puzzle ? (
        <p className="notice">Loading today’s puzzle…</p>
      ) : (
        <PlayScreen
          key={`${puzzle.date}:${puzzle.difficulty}`}
          initial={initial}
          title={
            <span>
              Daily · <span className="diff-tag">{puzzle.difficulty}</span>
            </span>
          }
          onSolved={submit}
          solvedContent={
            <div className="solved-info">
              {result ? (
                <>
                  <p>
                    <strong>{result.points}</strong> points · streak{' '}
                    <strong>{result.streak.current}</strong> (best {result.streak.longest})
                  </p>
                  <Link to="/leaderboard">See the leaderboard →</Link>
                </>
              ) : pending ? (
                <p>Saved — we’ll submit your result and score it when you’re back online.</p>
              ) : (
                <p>Saving your result…</p>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}
